import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Container, makeStyles, useTheme, TableRow, TableCell, IconButton, Grid,
  Button,
} from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { Column } from 'material-table';

import { Auth0Context } from '../../utils/auth0';
import LoadingErrorContainer from '../../components/LoadingErrorContainer';
import {
  loadPatients, selectPatients,
} from '../../store/slices/patientsSlice';
import { getFunctionPromisScore } from '../../utils/survey';
import { tableIcons } from '../../config/table';
import PromisScore from '../../components/PromisScore';
import { SurveyData, PatientData } from '../../generated-api';
import MaterialTableWithHeaders from
  '../../components/MaterialTableWithHeaders';
import PromisKey from '../../components/PromisKey';
import { exportCsv, CustomColumn } from '../../utils/tables';
import { selectOrganizations } from '../../store/slices/organizationsSlice';

enum ReassessmentStatus {
  Scheduled = 0,
  Incomplete = 1,
  Missed = 2,
}

interface OutcomeMeasuresRow {
  patient: PatientData;
  firstName: string;
  lastName: string;
  email: string;
  program: string;
  programStartedAt: string | null;
  nextReassessmentStartDate: Date | null;
  nextReassessmentEndDate: Date | null;
  nextReassessmentStatus: ReassessmentStatus | null;
  [surveyKey: string]: PatientData | Date | number | string | null;
}

export default function OutcomeMeasuresScreen() {
  const classes = useStyles();
  const theme = useTheme();
  const { user, getAccessTokenSilently } = useAuth0() as Auth0Context;

  const dispatch = useDispatch();
  const {
    patients, loading, error,
  } = useSelector(selectPatients);
  const { myOrganization } = useSelector(selectOrganizations);

  useEffect(() => {
    dispatch(loadPatients(getAccessTokenSilently));
  }, [getAccessTokenSilently]);

  const patientRows = patients == null
    ? null : patients.map(buildOutcomeMeasuresRow);

  const emailPatientsQuery = Object.entries({
    bcc: patientRows == null ? '' : patientRows
      .filter((patientRow) =>
        patientRow.nextReassessmentStatus === ReassessmentStatus.Incomplete)
      .map((patientRow) => patientRow.email)
      .join(','),
    subject: 'Pain and Function Check-In from Your Doc!',
    body: `Hello,\n\nHope you are well. This is ${myOrganization?.name}.\n\n`
      + 'We wanted to check in with you as you currently have a pending '
      + 'check-in for your pain and function. Please visit the Limber Health '
      + 'mobile app to complete your check in so your physician may follow '
      + 'your progress.\n\n'
      + `Thank you!\n\nIn health,\n${myOrganization?.name}`,
  }).map((kv) => kv.map(encodeURIComponent).join('=')).join('&');

  const emailNewPatientsQuery = Object.entries({
    bcc: patientRows == null ? '' : patientRows
      .filter((patientRow) =>
        patientRow.nextReassessmentStatus === ReassessmentStatus.Incomplete)
      .filter((patient) => {
        const diff = Math.abs(moment(patient.programStartedAt)
          .diff(moment.now(), 'weeks'));
        return diff <= 12;
      })
      .map((patientRow) => patientRow.email)
      .join(','),
    subject: 'Pain and Function Check-In from Your Doc!',
    body: `Hello,\n\nHope you are well. This is ${myOrganization?.name}.\n\n`
        + 'We wanted to check in with you as you currently have a pending '
        + 'check-in for your pain and function. Please visit the Limber Health '
        + 'mobile app to complete your check in so your physician may follow '
        + 'your progress.\n\n'
        + `Thank you!\n\nIn health,\n${myOrganization?.name}`,
  }).map((kv) => kv.map(encodeURIComponent).join('=')).join('&');

  const emailPatientsHref = `mailto:${user?.email}?${emailPatientsQuery}`;
  const emailNewPatientsHref = `mailto:${user?.email}?${emailNewPatientsQuery}`;

  return (
    <Container className={classes.container}>
      <LoadingErrorContainer
        loading={patientRows == null && loading}
        error={error && 'An error has occured.'}
        className={(!(loading && patientRows == null) && !error)
          ? classes.tableContainer : undefined}
      >
        <Grid container className={classes.controlsContainer}>
          <Grid item xs={6}>
            <Button
              variant="contained"
              color="primary"
              component="a"
              href={emailPatientsHref}
              startIcon={<EmailIcon />}
            >
              Email Patients with Pending Outcome Measures
            </Button>
            <Button
                variant="contained"
                color="primary"
                component="a"
                href={emailNewPatientsHref}
                startIcon={<EmailIcon />}
            >
              Email Patients with Pending Outcome Measures (First 12 Weeks)
            </Button>
          </Grid>
          <Grid item xs={6} className={classes.controlsRight}>
            <PromisKey />
          </Grid>
          <Grid item xs={6}></Grid>
        </Grid>
        {patientRows != null && (
          <MaterialTableWithHeaders
            columns={[
              {
                title: 'First Name',
                field: 'firstName',
                searchable: true,
                type: 'string',
                cellStyle,
              },
              {
                title: 'Last Name',
                field: 'lastName',
                searchable: true,
                type: 'string',
                cellStyle,
              },
              {
                title: 'Program',
                field: 'program',
                filtering: true,
                type: 'string',
                cellStyle,
              },
              {
                title: 'Date',
                field: 'nextReassessmentStartDate',
                type: 'date',
                render: (row) => row.nextReassessmentStartDate != null && (
                  moment(row.nextReassessmentStartDate).format('M/D/YYYY')
                  + '\xa0– '
                  + moment(row.nextReassessmentEndDate).format('M/D/YYYY')
                ),
                cellStyle,
                export: false,
              },
              {
                title: 'Status',
                field: 'nextReassessmentStatus',
                lookup: {
                  [ReassessmentStatus.Scheduled]: 'Scheduled',
                  [ReassessmentStatus.Incomplete]: 'Incomplete',
                  [ReassessmentStatus.Missed]: 'Missed Recent Follow-Up',
                },
                customSort: (row1, row2) =>
                  (row1.nextReassessmentStatus as number | null || 0)
                  - (row2.nextReassessmentStatus as number | null || 0),
                type: 'string',
                cellStyle,
                export: false,
              },
              {
                title: 'Email Patient',
                field: 'email',
                render: (row) => (
                  <IconButton component="a" href={`mailto:${row.email}`} >
                    <EmailIcon />
                  </IconButton>
                ),
                type: 'string',
                cellStyle,
              },
              ...makeSurveyColumns(0),
              ...makeSurveyColumns(1),
              ...makeSurveyColumns(2),
              ...makeSurveyColumns(3),
              ...makeSurveyColumns(4),
              ...makeSurveyColumns(5),
              ...makeSurveyColumns(6),
            ]}
            data={patientRows}
            title="PROMIS® Pain and Function Outcome Measures"
            icons={tableIcons}
            extraHeaders={(
              <TableRow className={classes.headerGroupsRow}>
                <TableCell colSpan={3} className={classes.stickyHeaderGroup} />
                <TableCell colSpan={2} className={classes.headerGroupCell}>
                  <span>Next Scheduled Follow-Up</span>
                </TableCell>
                <TableCell colSpan={1} />
                <TableCell colSpan={3} className={classes.headerGroupCell}>
                  <span>Baseline &ndash; Start of Program</span>
                </TableCell>
                <TableCell colSpan={3} className={classes.headerGroupCell}>
                  <span>4 Week Follow-Up</span>
                </TableCell>
                <TableCell colSpan={3} className={classes.headerGroupCell}>
                  <span>8 Week Follow-Up</span>
                </TableCell>
                <TableCell colSpan={3} className={classes.headerGroupCell}>
                  <span>3 Month Follow-Up</span>
                </TableCell>
                <TableCell colSpan={3} className={classes.headerGroupCell}>
                  <span>6 Month Follow-Up</span>
                </TableCell>
                <TableCell colSpan={3} className={classes.headerGroupCell}>
                  <span>1 Year Follow-Up</span>
                </TableCell>
                <TableCell colSpan={3} className={classes.headerGroupCell}>
                  <span>2 Year Follow-Up</span>
                </TableCell>
              </TableRow>
            )}
            options={{
              exportButton: true,
              exportAllData: true,
              exportCsv: exportCsv('Limber Outcome Measures'),
              pageSize: 50,
              pageSizeOptions: [20, 50, 100],
            }}
          />
        )}
      </LoadingErrorContainer>
    </Container>
  );
}

function buildOutcomeMeasuresRow(patient: PatientData): OutcomeMeasuresRow {

  const row: OutcomeMeasuresRow = {
    patient,
    firstName: patient.firstName,
    lastName: patient.lastName,
    email: patient.email,
    program: patient.program,
    programStartedAt: patient.programStartedAt || null,
    nextReassessmentStartDate: null,
    nextReassessmentEndDate: null,
    nextReassessmentStatus: null,
  };

  row.survey0Date = patient.surveys[0].date;
  row.survey0Pain = patient.surveys[0].promisScores.painInterference;
  row.survey0Function =
    getFunctionPromisScore(patient.surveys[0], patient.program);

  const now = moment();
  let previousWindowEnd = moment(patient.surveys[0].date);
  for (
    let surveyIndex = 1;
    surveyIndex <= patient.reassessmentWindows.length;
    surveyIndex += 1
  ) {
    const reassessmentWindow = patient.reassessmentWindows[surveyIndex - 1];
    const windowStart = moment(reassessmentWindow.start);
    const windowEnd = moment(reassessmentWindow.end);

    // See if there was a survey during this window. We count any survey
    // done between the end of the previous window and the end of this
    // window, since if the user has multiple programs they might not be
    // on the usual schedule.
    // eslint-disable-next-line no-loop-func
    const windowSurvey = patient.surveys.find((survey) => (
      moment(survey.date).isAfter(previousWindowEnd)
      && moment(survey.date).isBefore(windowEnd)
    ));

    let reassessmentStatus: ReassessmentStatus | null = null;
    if (windowSurvey != null) {
      row[`survey${surveyIndex}Date`] = windowSurvey.date;
      row[`survey${surveyIndex}Pain`] =
        windowSurvey.promisScores.painInterference;
      row[`survey${surveyIndex}Function`] =
        getFunctionPromisScore(windowSurvey, patient.program);
    } else if (
      now.isAfter(windowEnd)
      && now.clone().subtract(1, 'week').isBefore(windowEnd)
    ) {
      // If the end of the window was within the past week, then mark it
      // as missed.
      reassessmentStatus = ReassessmentStatus.Missed;
    } else if (windowStart.isAfter(now)) {
      reassessmentStatus = ReassessmentStatus.Scheduled;
    } else if (now.isAfter(windowStart) && now.isBefore(windowEnd)) {
      reassessmentStatus = ReassessmentStatus.Incomplete;
    }
    if (reassessmentStatus != null) {
      row.nextReassessmentStartDate = reassessmentWindow.start;
      row.nextReassessmentEndDate = reassessmentWindow.end;
      row.nextReassessmentStatus = reassessmentStatus;
      break;
    }

    previousWindowEnd = windowEnd;
  }

  return row;
}

const makeSurveyColumns = (
  surveyIndex: number,
): Array<CustomColumn<OutcomeMeasuresRow>> => [
  {
    title: 'Date',
    exportTitle: [
      'Baseline Assessment Date',
      '4 Week Follow-Up Date',
      '8 Week Follow-Up Date',
      '3 Month Follow-Up Date',
      '6 Month Follow-Up Date',
      '1 Year Follow-Up Date',
      '2 Year Follow-Up Date',
    ][surveyIndex],
    field: `survey${surveyIndex}Date`,
    type: 'date',
    cellStyle,
  },
  {
    title: 'Pain',
    field: `survey${surveyIndex}Pain`,
    type: 'numeric',
    render: (row) => row[`survey${surveyIndex}Pain`] != null && (
      <PromisScore
        score={row[`survey${surveyIndex}Pain`] as number}
        type="pain"
        patientHistory={row.patient}
      />
    ),
    cellStyle,
  },
  {
    title: 'Function',
    field: `survey${surveyIndex}Function`,
    type: 'numeric',
    render: (row) => row[`survey${surveyIndex}Function`] != null && (
      <PromisScore
        score={row[`survey${surveyIndex}Function`] as number}
        type="function"
        patientHistory={row.patient}
      />
    ),
    cellStyle,
  },
];

const cellStyle: any = {
  color: null,
  fontSize: null,
  fontFamily: null,
  fontWeight: null,
};

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(3) + 64,
    paddingBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
  },
  tableContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '& > .MuiPaper-root:last-child': {
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    '& > .MuiPaper-root:last-child > div:nth-child(2) > div > div': {
      overflowY: 'visible !important',
    },
    '& th': {
      position: 'sticky',
      backgroundColor: theme.palette.background.paper,
      zIndex: 12,
    },
    '& thead > tr:first-child > th': {
      top: 0,
      '&:first-child': {
        zIndex: 13,
      },
    },
    '& thead > tr:last-child > th': {
      top: 40,
      '&:nth-child(-n+3)': {
        zIndex: 13,
      },
    },
    '& thead > tr:last-child, & tbody > tr': {
      '& > :nth-child(-n+3)': {
        position: 'sticky',
        width: 100,
        zIndex: 11,
        backgroundColor: theme.palette.background.paper,
      },
      '& > *:nth-child(1)': {
        left: 0,
      },
      '& > *:nth-child(2)': {
        left: 100 + theme.spacing(4),
      },
      '& > *:nth-child(3)': {
        left: 200 + theme.spacing(8),
      },
    },
  },
  controlsContainer: {
    marginBottom: theme.spacing(2),
  },
  controlsRight: {
    textAlign: 'right',
  },
  headerGroupsRow: {
    '& th': {
      fontWeight: 'bold',
      textAlign: 'center',
      borderBottom: 'none',
      paddingBottom: 0,
      whiteSpace: 'nowrap',
    },
  },
  stickyHeaderGroup: {
    minWidth: 300 + theme.spacing(8),
    position: 'sticky',
    left: 0,
    zIndex: 11,
    backgroundColor: theme.palette.background.paper,
  },
  headerGroupCell: {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
    position: 'relative',
    maxLines: 2,
    '&::before': {
      borderTop: `1px solid ${theme.palette.grey[500]}`,
      content: '""',
      margin: '0 auto',
      position: 'absolute',
      top: `calc(0.75em + ${theme.spacing(2)}px)`,
      left: 0,
      right: 0,
      bottom: 0,
      width: '95%',
    },
    '& > span': {
      backgroundColor: theme.palette.background.paper,
      position: 'relative',
      zIndex: 1,
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
    },
  },
}));
